import type { TenantSummary } from "@praesto/core";

/**
 * Trial state of a tenant as returned by `GET /api/superadmin/tenants`
 * (`functions/src/lib/trial.ts`). `null` = no trial: a contract tenant or one
 * created before the field existed.
 */
export interface TenantTrial {
  readonly endsAt: string;
  readonly expired: boolean;
  readonly daysSinceEnd: number;
}

/**
 * `TenantSummary` lives in `@praesto/core`, whose changes need a coordinated
 * review; the console is the only reader of `trial`, so the field is declared
 * here. Older function deployments omit it — treated as "no trial".
 */
export type TenantRow = TenantSummary & { readonly trial?: TenantTrial | null };

export type TrialFilter = "all" | "trial" | "expired";

/** "in prova fino al 28/11/2026", "prova scaduta da 3 giorni", or null. */
export function trialLabel(trial: TenantTrial | null | undefined): string | null {
  if (!trial) return null;
  if (!trial.expired) {
    const date = new Intl.DateTimeFormat("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "Europe/Rome",
    }).format(new Date(trial.endsAt));
    return `In prova fino al ${date}`;
  }
  if (trial.daysSinceEnd === 0) return "Prova scaduta oggi";
  if (trial.daysSinceEnd === 1) return "Prova scaduta da 1 giorno";
  return `Prova scaduta da ${trial.daysSinceEnd} giorni`;
}

/**
 * `expired` lists the tenants past their 30 days, longest overdue first — the
 * ones to invoice or to suspend by hand. `trial` lists the running trials,
 * closest to the end first.
 */
export function filterByTrial(
  rows: ReadonlyArray<TenantRow>,
  filter: TrialFilter,
): ReadonlyArray<TenantRow> {
  if (filter === "all") return rows;
  if (filter === "expired") {
    return rows
      .filter((r) => r.trial?.expired === true)
      .sort((a, b) => b.trial!.daysSinceEnd - a.trial!.daysSinceEnd);
  }
  return rows
    .filter((r) => r.trial != null && !r.trial.expired)
    .sort((a, b) => a.trial!.endsAt.localeCompare(b.trial!.endsAt));
}
